import z from "zod";
import subIndustries from "./subindustries";
import industries from "./industries";
import { currency } from "./strings";
import {
  CollateralType,
  DiligenceStatus,
  Industry,
  LenderApplicationStatus,
  LenderOfferStatus,
  LoanApplicationOfferStatus,
  LoanApplicationStatus,
  LoanApplicationViewModel,
  LoanType,
  LoanUse,
  MetricQuality,
  PortalType,
  ProfileFileType,
  SubIndustry,
} from "@cxm/client";
type AssetType = "RealEstate" | "Equipment" | "Inventory" | "Other";
export const AssetType = {
  REAL_ESTATE: "RealEstate",
  EQUIPMENT: "Equipment",
  INVENTORY: "Inventory",
  OTHER: "Other",
} as const;

export enum HighLevelStatus {
  New,
  InReview,
  NonBinding,
  Binding,
  Closing,
  Declined,
  Cancelled,
  Closed,
  Missed,
  NotPursued,
  NotFunded,
  Lead,
  InUnderwriting,
  OnHold,
}

export const getExpectedEndTimeInStatus = (
  loanApp: LoanApplicationViewModel,
) => {
  const updated = new Date(loanApp.lastStatusChange ?? 0);
  let referenceDate = updated;
  if (loanApp.status == LoanApplicationStatus.STARTED) {
    referenceDate = new Date(loanApp.createdAt ?? 0);
  }
  const remaining = expectedStatusTimes.find((x) => x.status == loanApp.status);

  if (!remaining) {
    return referenceDate;
  }

  if (remaining?.days >= 1) {
    referenceDate.setDate(referenceDate.getDate() + remaining?.days);
    return referenceDate;
  } else {
    referenceDate.setHours(referenceDate.getHours() + (remaining?.days * 24));
    return referenceDate;
  }
};

const expectedStatusTimes = [
  {
    status: LoanApplicationStatus.STARTED,
    days: 10,
  },
  {
    status: LoanApplicationStatus.SUBMITTED,
    days: 0.5,
  },
  {
    status: LoanApplicationStatus.SPECIALIST_REVIEW,
    days: 1,
  },
  {
    status: LoanApplicationStatus.SPECIALIST_REVIEW_COMPLETE,
    days: 1,
  },
  {
    status: LoanApplicationStatus.COMPLETED_SECOND_ATTESTATION,
    days: 1,
  },
  {
    status: LoanApplicationStatus.LENDERS_REVIEW,
    days: 30,
  },
  {
    status: LoanApplicationStatus.OFFER_ACCEPTED,
    days: 1,
  },
  {
    status: LoanApplicationStatus.LENDER_BORROWER_CALL_COMPLETE,
    days: 21,
  },
  {
    status: LoanApplicationStatus.SPECIALIST_BTS_REVIEW,
    days: 0.5,
  },
  {
    status: LoanApplicationStatus.BTS_SUBMITTED,
    days: 5,
  },
  {
    status: LoanApplicationStatus.BTS_SIGNED_BY_BORROWER,
    days: 0.5,
  },
  {
    status: LoanApplicationStatus.SIGNED_BTS_APPROVED,
    days: 3,
  },
  {
    status: LoanApplicationStatus.SPECIALIST_CLOSING_DOCUMENTS_REVIEW,
    days: 0.5,
  },
  {
    status: LoanApplicationStatus.CLOSING_DOCUMENTS_SUBMITTED,
    days: 3,
  },
  {
    status: LoanApplicationStatus.SPECIALIST_CLOSING_DOCUMENTS_REVIEW,
    days: 1,
  },
  {
    status: LoanApplicationStatus.SIGNED_CLOSING_DOCUMENTS_APPROVED,
    days: 2,
  },
  {
    status: LoanApplicationStatus.SPECIALIST_FINAL_CLOSING_DOCUMENTS_REVIEW,
    days: 0.5,
  },
  {
    status: LoanApplicationStatus.SPECIALIST_FINAL_CLOSING_DOCUMENTS_APPROVED,
    days: 2,
  },
  {
    status: LoanApplicationStatus.LOAN_FUNDED,
    days: 2,
  },
];

export const getExpectedFundsDate = (loanApp: LoanApplicationViewModel) => {
  const expectedCloseDate = getExpectedCloseDate(loanApp);
  if (!loanApp.expectedFundsDate) {
    return expectedCloseDate;
  }

  const expectedFunds = new Date(loanApp.expectedFundsDate);
  if (
    expectedFunds > new Date() && expectedFunds > expectedCloseDate
  ) {
    return expectedFunds;
  }

  return expectedCloseDate;
};
export const getExpectedCloseDate = (
  loanApp: LoanApplicationViewModel,
) => {
  if (
    loanApp.expectedCloseDate &&
    new Date(loanApp.expectedCloseDate) > new Date()
  ) {
    return new Date(loanApp.expectedCloseDate);
  }
  const updated = new Date(loanApp.lastStatusChange ?? 0);
  let referenceDate = updated;
  if (loanApp.status == LoanApplicationStatus.STARTED) {
    referenceDate = new Date(loanApp.createdAt ?? 0);
  }
  const remainingIndex = expectedStatusTimes.findIndex((x) =>
    x.status == loanApp.status
  );
  if (remainingIndex == -1) {
    return referenceDate;
  }

  for (var i = remainingIndex; i < expectedStatusTimes.length; i++) {
    var days = expectedStatusTimes[i]?.days ?? 1;

    if (days >= 1) {
      referenceDate.setDate(referenceDate.getDate() + days);
    } else {
      referenceDate.setHours(referenceDate.getHours() + (days * 24));
    }

    if (i == remainingIndex) {
      if (new Date() > referenceDate) {
        referenceDate = new Date();
      }
    }
  }

  return referenceDate;
};

export const collateralTypeOptions = [
  { label: "Equipment", value: CollateralType.EQUIPMENT },
  { label: "Receivables", value: CollateralType.RECEIVABLES },
  { label: "RealEstate", value: CollateralType.REAL_ESTATE },
  { label: "Inventory", value: CollateralType.INVENTORY },
];

export const fileTypeOptions = [
  {
    label: "Additional Docs",
    value: ProfileFileType.ADDITIONAL,
  },
  {
    label: "Org Chart",
    value: ProfileFileType.ORG_CHART,
  },
  {
    label: "Business Plan",
    value: ProfileFileType.BUSINESS_PLAN,
  },
  {
    label: "Customer Contract",
    value: ProfileFileType.CUSTOMER_CONTRACT,
  },
  {
    label: "Tax Returns",
    value: ProfileFileType.TAX_RETURNS,
  },
  {
    label: "Last Year Bank Statements",
    value: ProfileFileType.LAST_YEAR_BANK_STATEMENTS,
  },
  {
    label: "Historical P&L Docs",
    value: ProfileFileType.HISTORICAL_PLFILES,
  },
  {
    label: "Forecasted P&L Docs",
    value: ProfileFileType.FORECAST_PLFILES,
  },
  {
    label: "Most Recent Balance Sheet",
    value: ProfileFileType.MOST_RECENT_BALANCE_SHEET,
  },
  {
    label: "Previous Years Balance Sheet",
    value: ProfileFileType.PREVIOUS_YEARS_BALANCE_SHEET,
  },
  {
    label: "Accounts Receivable Schedule",
    value: ProfileFileType.ACCOUNTS_RECEIVABLE_SCHEDULE,
  },
  {
    label: "Accounts Payable Schedule",
    value: ProfileFileType.ACCOUNTS_PAYABLE_SCHEDULE,
  },
  {
    label: "Inventory Supporting Docs",
    value: ProfileFileType.INVENTORY_SUPPORTING,
  },
  {
    label: "Inventory Breakdown",
    value: ProfileFileType.INVENTORY_BREAKDOWN,
  },
  {
    label: "Equipment Breakdown",
    value: ProfileFileType.EQUIPMENT_BREAKDOWN,
  },
  {
    label: "Real Estate Breakdown",
    value: ProfileFileType.REAL_ESTATE_BREAKDOWN,
  },
  {
    label: "Real Estate Appraisal",
    value: ProfileFileType.REAL_ESTATE_APPRAISAL,
  },
  {
    label: "Additional Debt Schedule",
    value: ProfileFileType.ADDITIONAL_DEBT,
  },
];

export const specialistLoanStatusOptions = [
  {
    label: "New",
    value: HighLevelStatus.New,
  },
  {
    label: "In Review",
    value: HighLevelStatus.InReview,
  },
  {
    label: "Non-Binding Offer",
    value: HighLevelStatus.NonBinding,
  },
  {
    label: "Binding Offer",
    value: HighLevelStatus.Binding,
  },
  {
    label: "Due Diligence",
    value: HighLevelStatus.Closing,
  },
  {
    label: "Declined",
    value: HighLevelStatus.Declined,
  },
  {
    label: "Cancelled",
    value: HighLevelStatus.Cancelled,
  },
  {
    label: "Closed",
    value: HighLevelStatus.Closed,
  },
  {
    label: "Not Funded",
    value: HighLevelStatus.NotFunded,
  },
];

export const getLoanTypeColor = (type: LoanType) => {
  switch (type) {
    case LoanType.ABL:
      return "#00c88d";
    case LoanType.SBA:
      return "#00c7b1";
    case LoanType.SBIC:
      return "#ffb259";
    case LoanType.CASH_FLOW:
      return "#0095ff";
    case LoanType.EQUIPMENT:
      return "#002785";
    case LoanType.JUNIOR_MEZZ:
      return "#b30000";
    case LoanType.VENTURE_REV:
      return "#b30000";
    case LoanType.AR_FACTORING:
      return "#6b6b6b";
  }
};

export const getLoanTypeShortName = (type: LoanType) => {
  switch (type) {
    case LoanType.ABL:
      return "ABL";
    case LoanType.SBA:
      return "SBA";
    case LoanType.SBIC:
      return "SBIC";
    case LoanType.CASH_FLOW:
      return "Cash Flow";
    case LoanType.EQUIPMENT:
      return "Equipment";
    case LoanType.JUNIOR_MEZZ:
      return "Junior/Mezz";
    case LoanType.VENTURE_REV:
      return "Venture Rev";
    case LoanType.AR_FACTORING:
      return "AR";
  }
};

export const getStatusColor = (status: HighLevelStatus) => {
  switch (status) {
    case HighLevelStatus.New:
      return "#00c88d";
    case HighLevelStatus.InReview:
      return "#00c7b1";
    case HighLevelStatus.NonBinding:
      return "#ffb259";
    case HighLevelStatus.Binding:
      return "#0095ff";
    case HighLevelStatus.Closing:
      return "#002785";
    case HighLevelStatus.Declined:
      return "#b30000";
    case HighLevelStatus.Cancelled:
      return "#b30000";
    case HighLevelStatus.Closed:
      return "#6b6b6b";
  }
};

export const lenderLoanStatusOptions = [
  ...specialistLoanStatusOptions,
  {
    label: "Missed",
    value: HighLevelStatus.Missed,
  },
  {
    label: "Not Pursued",
    value: HighLevelStatus.NotPursued,
  },
];

export const isPreSubmit = (status: LoanApplicationStatus) =>
  [
    LoanApplicationStatus.SCREEN1,
    LoanApplicationStatus.SCREEN3,
    LoanApplicationStatus.STARTED,
    LoanApplicationStatus.SUBMITTED,
    LoanApplicationStatus.SPECIALIST_REVIEW,
    LoanApplicationStatus.SPECIALIST_REVIEW_COMPLETE,
  ].find((x) => x == status);

export const isPreFirstSubmit = (status: LoanApplicationStatus) =>
  [
    LoanApplicationStatus.SCREEN1,
    LoanApplicationStatus.SCREEN3,
    LoanApplicationStatus.STARTED,
  ].find((x) => x == status);

export const loanRatios = [
  "EBITDA_To_Revenue",
  "EBITDA_To_Loan_Amount",
  "Loan_Amount_To_Receivables",
  "Inventory_To_Loan_Amount",
  "PPE_To_Loan_Amount",
  "Cash_To_Loan_Amount",
  "Loan_Amount_To_Revenue",
];

export const metricQualities = [
  {
    quality: MetricQuality.BAD,
    color: "#FF0000",
  },
  {
    quality: MetricQuality.POOR,
    color: "#FF8000",
  },
  {
    quality: MetricQuality.FAIR,
    color: "#000000",
  },
  {
    quality: MetricQuality.GOOD,
    color: "#00CC00",
  },
  {
    quality: MetricQuality.GREAT,
    color: "#009900",
  },
  {
    quality: MetricQuality.EXCELLENT,
    color: "#3659d9",
  },
];

export const getLoanAppTitle = (x: LoanApplicationViewModel) => {
  return `${currency(getTotalLoanAmountFromAskAmounts(x.loanAskAmounts))} - ${loanTypes.find((y) => y.key == x.loanAskAmounts?.[0]?.loanType)?.fullName
    } - ${new Date(x.createdAt ?? 0).toLocaleString("en-US", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    })
    }`;
};

export const refinanceAssetOptions = [
  {
    label: "",
  },
];

export const loanUseOptions = [
  {
    label: "Acquisition",
    value: LoanUse.ACQUISITION,
    color: "#FF5733",
  },
  {
    label: "Growth Capital",
    value: LoanUse.GROWTH_CAPITAL,
    color: "#28A745",
  },
  {
    label: "General/Working Capital",
    value: LoanUse.GENERAL_WORKING_CAPITAL,
    color: "#007BFF",
  },
  {
    label: "Inventory",
    value: LoanUse.INVENTORY,
    color: "#FFC107",
  },
  {
    label: "Real Estate",
    value: LoanUse.REAL_ESTATE,
    color: "#FFC107",
  },
  {
    label: "Equipment",
    value: LoanUse.EQUIPMENT,
    color: "#FFC107",
  },
  {
    label: "Refinance",
    value: LoanUse.REFINANCE,
    color: "#6C757D",
  },
  {
    label: "Shareholder Liquidity",
    value: LoanUse.SHAREHOLDER_LIQUIDITY,
    color: "#DC3545",
  },
];

// Helper functions to derive data from loanAskAmounts for backward compatibility
export const getLoanTypesFromAskAmounts = (
  loanAskAmounts?: Array<{ loanType?: LoanType; amount?: number }> | null,
): LoanType[] => {
  if (!loanAskAmounts) return [];
  return loanAskAmounts
    .filter((entry) => entry.loanType && (entry.amount || 0) > 0)
    .map((entry) => entry.loanType!)
    .filter((type, index, array) => array.indexOf(type) === index); // Remove duplicates
};

export const getTotalLoanAmountFromAskAmounts = (
  loanAskAmounts?: Array<{ loanType?: LoanType; amount?: number }> | null,
): number => {
  if (!loanAskAmounts) return 0;
  return loanAskAmounts.reduce(
    (total, entry) => total + (entry.amount || 0),
    0,
  );
};

export const getLoanAmountByType = (
  loanType: LoanType,
  loanAskAmounts?: Array<{ loanType?: LoanType; amount?: number }> | null,
): number => {
  if (!loanAskAmounts) return 0;
  const entry = loanAskAmounts.find((entry) => entry.loanType === loanType);
  return entry?.amount || 0;
};

export const getPrimaryLoanType = (
  loanAskAmounts?: Array<{ loanType?: LoanType; amount?: number }> | null,
): LoanType | undefined => {
  if (!loanAskAmounts || loanAskAmounts.length === 0) return undefined;

  // Find the loan type with the highest amount
  const maxEntry = loanAskAmounts.reduce((max, current) => {
    if (!current.loanType || !current.amount) return max;
    if (!max || (current.amount > (max.amount || 0))) return current;
    return max;
  }, null as { loanType?: LoanType; amount?: number } | null);

  return maxEntry?.loanType;
};

export const loanApplicationModel = z.object({
  targetLoanAmount: z.number().optional(),
  loanAskAmounts: z.object({
    loanType: z.nativeEnum(LoanType),
    amount: z.number(),
  }).array()
    .nullish(),
  personalGuarantee: z.boolean(),
  loanUseSummary: z.string().min(10).nullish(),
});

export const loanApplicationModelWithSummary = loanApplicationModel.extend({
  loanUseSummary: z.string().min(10),
  loanUseDetails: z.object({
    loanUse: z.record(z.nativeEnum(LoanUse), z.number()).nullish(),
    purchaseUse: z.record(z.nativeEnum(AssetType), z.number()).nullish(),
  }).superRefine((use, ctx) => {
    const missingPurchase = Object.entries(use.loanUse ?? {}).find(([k, v]) => {
      if (v < 0.01) {
        return false;
      }
      if (k == "RealEstate" || k == "Equipment" || k == "Inventory") {
        const purchaseUse = use.purchaseUse && use.purchaseUse[k];
        return purchaseUse == undefined || purchaseUse <= 0.02;
      }
      return false;
    });
    if (missingPurchase != undefined) {
      ctx.addIssue({
        path: [""],
        code: z.ZodIssueCode.custom,
        message: "Asset purchases must have purchase amounts.",
      });
    }
    const total = Object.values(use.loanUse ?? {}).reduce((a, b) => (a + b), 0);
    if (total < 1) {
      ctx.addIssue({
        path: ["", ".."],
        code: z.ZodIssueCode.custom,
        message:
          "Entire loan amount must be allocated to a specific use. Use growth capital for miscellaneous use.",
      });
    }
  }),
});

export const loanTypes: {
  name: string;
  key: LoanType;
  color: string;
  id: LoanType;
  fullName: string;
  probability: number;
  weighting: number;
  shortDescription: string;
  longDescription: string;
}[] = [
    {
      name: "ABL",
      key: "Abl",
      fullName: "Asset Backed",
      color: "#7f47de",
      id: LoanType.ABL,
      probability: 1,
      weighting: 1,
      shortDescription: "Secured by business assets like inventory and equipment",
      longDescription:
        "Asset-Based Lending (ABL) provides credit lines secured by business assets like inventory, equipment, machinery, and real estate. Ideal for businesses with substantial tangible assets but need flexible credit access. What Underwriters Typically Look For: Asset quality and value (underwriters will loan 50-80% of inventory value, 70-90% of equipment value), how quickly inventory sells, business track record (minimum 2 years), monthly/quarterly asset reporting requirements, and how easily assets could be sold if the loan defaults.",
    },
    {
      name: "AR Factoring",
      key: "ArFactoring",
      fullName: "Accounts Receivable Factoring",
      color: "#93268f",
      id: LoanType.AR_FACTORING,
      probability: 1,
      weighting: 1,
      shortDescription: "Convert unpaid invoices into immediate cash",
      longDescription:
        "Accounts Receivable Factoring converts unpaid invoices into immediate working capital by selling receivables to a factoring company. Ideal for businesses with creditworthy customers but cash flow timing issues. What Underwriters Typically Look For: Quality of unpaid invoices and customer creditworthiness, invoice aging (prefer invoices less than 90 days old), customer concentration limits (typically no single customer represents more than 20% of total invoices), business operating history, and absence of legal claims on receivables (no other underwriters have rights to the invoices).",
    },
    {
      name: "PO Factoring",
      key: "PurchaseOrder",
      fullName: "Purchase Order Factoring",
      color: "#93268f",
      id: LoanType.PURCHASE_ORDER,
      probability: 1,
      weighting: 1,
      shortDescription:
        "Finance suppliers and fulfill orders with upfront capital",
      longDescription:
        "Purchase Order Factoring provides upfront capital to fulfill customer orders by financing the supply chain. Perfect for businesses with confirmed orders but insufficient working capital to complete them. What Underwriters Typically Look For: Creditworthy end customers with strong payment history, confirmed purchase orders with acceptable profit margins (typically 20%+ markup above costs), reliable suppliers with established relationships, clear delivery timelines and milestones, and verification of all parties involved.",
    },
    {
      name: "SBA",
      key: "Sba",
      fullName: "Small Business Administration",
      color: "#425dc8",
      id: LoanType.SBA,
      probability: 1,
      weighting: 1,
      shortDescription: "Government-backed loans with favorable terms",
      longDescription:
        "SBA Guaranteed Loans offer government-backed financing with two main programs: 7(a) Loans (up to $5M) for flexible business needs including working capital, business purchases, and equipment with variable rates and 90% security requirements (business assets worth 90% of loan amount); 504 Loans (up to $5.5M) specifically for real estate and heavy equipment with fixed rates, 10% down payments, and job creation requirements through a three-part structure (50% bank, 40% CDC, 10% borrower). New June 1, 2025 Criteria: 100% ownership by U.S. citizens/permanent residents required, minimum SBSS score increased to 165 for small loans (now $350K max), mandatory 10% cash injection for startups, reinstated tax transcript verification, hazard insurance required for loans >$50K, businesses with undocumented employees ineligible, and stricter pre-2021 underwriting standards restored. What Underwriters Typically Look For: Business must meet SBA size standards, personal credit scores 680+, adequate ability to make loan payments from business income, personal guarantees from 20%+ owners (owners personally responsible if business defaults), and compliance with citizenship requirements.",
    },
    {
      name: "Equipment",
      key: "Equipment",
      fullName: "Equipment",
      color: "#4abee4",
      id: LoanType.EQUIPMENT,
      probability: 1,
      weighting: 1,
      shortDescription: "Finance specific equipment purchases",
      longDescription:
        "(CHOOSE EQUIPMENT IN LOAN USE TO SEE EQUIPMENT LOAN INFORMATION.) Equipment financing offers secured lending for business equipment purchases with the equipment serving as security for the loan. Provides competitive rates and preserves working capital for operations. What Underwriters Typically Look For: Equipment type and useful life (loan term must match how long equipment will last), business time in operation (typically 2+ years), cash flow sufficient for monthly payments, down payment requirements (typically 10-20% of equipment cost), and personal guarantees from owners (owners personally responsible if business defaults).",
    },
    {
      name: "SBIC",
      key: "Sbic",
      fullName: "Small Business Investment Company",
      color: "#95de47",
      id: LoanType.SBIC,
      probability: 1,
      weighting: 1,
      shortDescription: "SBA-licensed equity and debt financing",
      longDescription:
        "Small Business Investment Company (SBIC) financing comes from privately owned investment funds licensed by the SBA that combine private capital with SBA-guaranteed debt to provide equity capital and long-term debt financing to high-growth potential small businesses. SBICs provide patient capital with strategic guidance and industry expertise, making them particularly suitable for businesses seeking growth capital and willing to give up some equity in exchange for funding and strategic guidance. What Underwriters Typically Look For: Strong management team with proven track record, scalable business model with significant growth potential, minimum $500K-$1M investment size, willingness to accept board participation (investors get a seat at the table for major decisions), and businesses that can triple to quintuple their value over 5-7 years.",
    },
    {
      name: "Cash Flow",
      key: "CashFlow",
      fullName: "Cash Flow",
      color: "#f37d0a",
      id: LoanType.CASH_FLOW,
      probability: 1,
      weighting: 1,
      shortDescription: "Based on business revenue and cash flow",
      longDescription:
        "Cash Flow-Based Lending provides unsecured capital based on business performance rather than requiring assets as security. Perfect for service businesses and companies with strong revenue but limited tangible assets. What Underwriters Typically Look For: Minimum 12-24 months operating history, consistent monthly revenue (typically $10K+ per month), positive operating profit and cash flow, business income that's at least 25% higher than all loan payments combined, personal credit scores 650+, and low existing debt compared to income.",
    },
    {
      name: "Junior/Mezz",
      key: "JuniorMezz",
      fullName: "Junior/Mezzanine",
      color: "#e44a72",
      id: LoanType.JUNIOR_MEZZ,
      probability: 1,
      weighting: 1,
      shortDescription: "Subordinated debt with equity features",
      longDescription:
        "Junior/Mezzanine Financing bridges the gap between debt and equity with flexible terms and growth-oriented structures. Ideal for expansion capital when traditional debt is insufficient. What Underwriters Typically Look For: Established business with minimum $5-10M revenue, strong operating profit margins (typically 15%+ of revenue after expenses), experienced management team, ability to make payments on current debt plus new payments, and willingness to provide equity participation through warrants (rights to buy company stock) or convertible features.",
    },
    {
      name: "Venture/Rev",
      key: "VentureRev",
      fullName: "Venture/Revenue",
      color: "#4ae4cc",
      id: LoanType.VENTURE_REV,
      probability: 1,
      weighting: 1,
      shortDescription: "Growth capital for high-potential businesses",
      longDescription:
        "Venture/Revenue Financing provides growth capital with flexible repayment terms tied to business performance. Ideal for high-growth companies with predictable revenue streams but limited operating history. What Underwriters Typically Look For: Strong monthly recurring revenue (subscription-based income that repeats each month) or predictable revenue model, high gross margins (typically 70%+ profit after direct costs), experienced management team, demonstrated product-market fit (proven customer demand), and low customer churn rates (customers don't cancel frequently).",
    },
  ];

export const loanTypeOptions = loanTypes.map((type) => ({
  value: type.key,
  label: type.name,
}));

export const loanTypeString = (loanType: LoanType) => {
  return loanTypes.find((x) => x.id == loanType)?.name || "Invalid";
};

export const industryOptions = Object.values(industries).map((industry) => ({
  label: industry.displayName,
  value: industry.displayName,
})).sort((a, b) => {
  if (a.label < b.label) {
    return -1;
  } else if (a.label > b.label) {
    return 1;
  }

  return 0;
});

export const numberedIndustryOptions = Object.values(Industry).map((
  industry,
  idx,
) => ({
  label: Object.values(industries).find((x) => x.id == idx - 1)?.displayName ??
    "",
  value: industry,
})).sort((a, b) => {
  if (a.label < b.label) {
    return -1;
  } else if (a.label > b.label) {
    return 1;
  }

  return 0;
}).filter((x) => x.value != Industry.UNKNOWN);

export const subIndustryOptions = Object.entries(subIndustries).map((
  [_, industry],
) => ({
  label: industry.name,
  value: industry.name,
  parent: Object.values(industries).find((x) => x.id == industry.parent)
    ?.displayName,
})).sort((a, b) => {
  if (a.label < b.label) {
    return -1;
  } else if (a.label > b.label) {
    return 1;
  }

  return 0;
});

export const numberedSubIndustryOptions = Object.entries(SubIndustry).map((
  [key, industry],
) => ({
  label:
    Object.values(subIndustries).find((x) => x.subIndustry == industry)?.name ??
    "",
  value: industry,
  parent: Object.values(
    Industry,
  )[
    (Object.values(subIndustries).find((x) => x.subIndustry == industry)
      ?.parent ?? 0) + 1
  ],
})).sort((a, b) => {
  if (a.label < b.label) {
    return -1;
  } else if (a.label > b.label) {
    return 1;
  }

  return 0;
});

export enum PublicLoanApplicationStatus {
  Unsubmitted,
  SpecialistReview,
  ReadyForBorrowerSubmit,
  AwaitingOffers,
  SpecialistReviewingOffers,
  BorrowerReviewingOffers,
  AwaitingBindingOffer,
  SpecialistReviewingBindingOffer,
  BorrowerReviewingBindingOffer,
  AwaitingClosingDocs,
  BorrowerSigningClosingDocs,
  SpecialistReviewingSignedClosingDocs,
  AwaitingClose,
  Missed,
  Declined,
  Closed,
}

export enum ApplicationStatusMessage {
  InitialSubmission,
  IntroductoryCall,
  BorrowerReview,
  BorrowerFinalAttest,
  LenderSubmission,
  ReviewLenderSubmission,
  LenderCreatingNonBindingOffer,
  NonBindingOfferNeedsRevision,
  SpecialistReviewingNonBindingOffers,
  BorrowerReviewingNonBindingOffers,
  LenderIntroCall,
  LenderCreateBindingOffer,
  BindingOfferNeedsRevision,
  SpecialistReviewingBindingOffer,
  BorrowerReviewingBindingOffer,
  SpecialistReviewingSignedBTS,
  LenderCreatingClosingDocs,
  SpecialistReviewingClosingDocs,
  BorrowerReviewingClosingDocs,
  SpecialistReviewingSignedClosingDocs,
  LenderCreatingFinalClosingDocuments,
  SpecialistReviewingFinalClosingDocuments,
  ConfirmingFunding,
  FinalClose,
  Closed,
  Cancelled,
  Missed,
  Declined,
  NotPursued,
  NotFunded,
  SupervisorApproval,
  InUnderwriting,
  OnHold,
}

export const getHighLevelStatusSteps = (portalType: PortalType) => {
  switch (portalType) {
    case PortalType.BORROWER:
      return [
        HighLevelStatus.InReview,
        HighLevelStatus.NonBinding,
        HighLevelStatus.Binding,
        HighLevelStatus.Closing,
        HighLevelStatus.Closed,
      ];
    case PortalType.LENDER:
      return [
        HighLevelStatus.NonBinding,
        HighLevelStatus.Binding,
        HighLevelStatus.Closing,
        HighLevelStatus.Closed,
      ];
    case PortalType.MANAGEMENT:
      return [
        HighLevelStatus.InReview,
        HighLevelStatus.NonBinding,
        HighLevelStatus.Binding,
        HighLevelStatus.Closing,
        HighLevelStatus.Closed,
      ];
  }
};

export const getShortHighLevelStatusMessageString = (
  statusMessage: HighLevelStatus,
) => {
  switch (statusMessage) {
    case HighLevelStatus.New:
      return "New";
    case HighLevelStatus.InReview:
      return "Approval";
    case HighLevelStatus.NonBinding:
      return "Lenders";
    case HighLevelStatus.Binding:
      return "Binding";
    case HighLevelStatus.Closing:
      return "Closing";
    case HighLevelStatus.Closed:
      return "Closed";
    case HighLevelStatus.Cancelled:
      return "Cancelled";
    case HighLevelStatus.Missed:
      return "Missed";
    case HighLevelStatus.NotPursued:
      return "Missed";
    case HighLevelStatus.NotFunded:
      return "Not Funded";
    case HighLevelStatus.Lead:
      return "Lead";
    case HighLevelStatus.InUnderwriting:
      return "Underwriting";
  }
};

export const getHighLevelStatusMessageString = (
  statusMessage: HighLevelStatus,
) => {
  switch (statusMessage) {
    case HighLevelStatus.New:
      return "New";
    case HighLevelStatus.InReview:
      return "Application Review";
    case HighLevelStatus.NonBinding:
      return "Non-Binding Offer";
    case HighLevelStatus.Binding:
      return "Binding Offer";
    case HighLevelStatus.Closing:
      return "Due Diligence";
    case HighLevelStatus.Closed:
      return "Closed";
    case HighLevelStatus.Cancelled:
      return "Cancelled";
    case HighLevelStatus.Missed:
      return "Missed";
    case HighLevelStatus.NotPursued:
      return "Missed";
    case HighLevelStatus.NotFunded:
      return "Not Funded";
    case HighLevelStatus.Lead:
      return "Lead";
    case HighLevelStatus.InUnderwriting:
      return "Underwriting";
    case HighLevelStatus.OnHold:
      return "On Hold";
  }
};

export const getHighLevelStatusMessageDescription = (
  statusMessage: HighLevelStatus,
) => {
  switch (statusMessage) {
    case HighLevelStatus.New:
      return "Initial application setup";
    case HighLevelStatus.InReview:
      return "Specialist approves loan and borrower profile details.";
    case HighLevelStatus.NonBinding:
      return "Non-Binding offers are received from lenders and the borrower accepts one to move forward with.";
    case HighLevelStatus.Binding:
      return "A final Binding Offer is submitted by the lender for approval and signature by the borrower.";
    case HighLevelStatus.Closing:
      return "Closing documents are prepared and signed.";
    case HighLevelStatus.Closed:
      return "Loan is complete and funds are transferred.";
    case HighLevelStatus.Cancelled:
      return "Cancelled";
    case HighLevelStatus.Missed:
      return "Missed";
    case HighLevelStatus.NotPursued:
      return "Missed";
    case HighLevelStatus.NotFunded:
      return "Loan could not secure funding.";
    case HighLevelStatus.OnHold:
      return "Loan is on hold.";
  }
};

export const getStepsComplete = (
  status: ApplicationStatusMessage,
  portalType: PortalType,
) => {
  const highLevelSteps = getHighLevelStatusSteps(portalType);
  let stepsBeforeStatus = 0;
  let totalSteps = 0;
  let foundStep = false;
  for (var i = 0; i < highLevelSteps.length; i++) {
    const step = highLevelSteps[i];
    if (step != undefined) {
      var innerSteps = getHighLevelStatusProgressBar(
        step,
        portalType,
      );
      if (innerSteps != undefined) {
        for (var j = 0; j < innerSteps?.length; j++) {
          totalSteps++;
          if (!foundStep) {
            stepsBeforeStatus++;
          }
          if (innerSteps[j] == status) {
            foundStep = true;
          }
        }
      }
    }
  }

  if (
    status == ApplicationStatusMessage.Missed ||
    status == ApplicationStatusMessage.NotPursued ||
    status == ApplicationStatusMessage.Declined
  ) {
    return {
      stepsComplete: 2,
      totalSteps: totalSteps,
    };
  }

  if (status == ApplicationStatusMessage.Closed) {
    return {
      stepsComplete: stepsBeforeStatus + 1,
      totalSteps: totalSteps + 1,
    };
  }

  return {
    stepsComplete: stepsBeforeStatus,
    totalSteps: totalSteps + 1,
  };
};

export const getHighLevelStatusProgressBar = (
  status: HighLevelStatus,
  portalType: PortalType,
) => {
  switch (portalType) {
    case PortalType.MANAGEMENT:
      switch (status) {
        case HighLevelStatus.New:
          return [];
        case HighLevelStatus.InReview:
          return [
            ApplicationStatusMessage.InitialSubmission,
            ApplicationStatusMessage.IntroductoryCall,
            ApplicationStatusMessage.BorrowerReview,
            ApplicationStatusMessage.BorrowerFinalAttest,
            ApplicationStatusMessage.LenderSubmission,
          ];
        case HighLevelStatus.NonBinding:
          return [
            ApplicationStatusMessage.LenderCreatingNonBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingNonBindingOffers,
            ApplicationStatusMessage.BorrowerReviewingNonBindingOffers,
            ApplicationStatusMessage.LenderIntroCall,
          ];
        case HighLevelStatus.Binding:
          return [
            ApplicationStatusMessage.LenderCreateBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingBindingOffer,
            ApplicationStatusMessage.BorrowerReviewingBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingSignedBTS,
          ];
        case HighLevelStatus.Closing:
          return [
            ApplicationStatusMessage.LenderCreatingClosingDocs,
            ApplicationStatusMessage.SpecialistReviewingClosingDocs,
            ApplicationStatusMessage.BorrowerReviewingClosingDocs,
            ApplicationStatusMessage.SpecialistReviewingSignedClosingDocs,
            ApplicationStatusMessage.LenderCreatingFinalClosingDocuments,
            ApplicationStatusMessage.FinalClose,
          ];
        case HighLevelStatus.Closed:
          return [];
      }
      break;
    case PortalType.BORROWER:
      switch (status) {
        case HighLevelStatus.New:
          return [];
        case HighLevelStatus.InReview:
          return [
            ApplicationStatusMessage.InitialSubmission,
            ApplicationStatusMessage.IntroductoryCall,
            ApplicationStatusMessage.BorrowerReview,
            ApplicationStatusMessage.BorrowerFinalAttest,
            ApplicationStatusMessage.LenderSubmission,
          ];
        case HighLevelStatus.NonBinding:
          return [
            ApplicationStatusMessage.LenderCreatingNonBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingNonBindingOffers,
            ApplicationStatusMessage.BorrowerReviewingNonBindingOffers,
            ApplicationStatusMessage.LenderIntroCall,
          ];
        case HighLevelStatus.Binding:
          return [
            ApplicationStatusMessage.LenderCreateBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingBindingOffer,
            ApplicationStatusMessage.BorrowerReviewingBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingSignedBTS,
          ];
        case HighLevelStatus.Closing:
          return [
            ApplicationStatusMessage.LenderCreatingClosingDocs,
            ApplicationStatusMessage.SpecialistReviewingClosingDocs,
            ApplicationStatusMessage.BorrowerReviewingClosingDocs,
            ApplicationStatusMessage.SpecialistReviewingSignedClosingDocs,
            ApplicationStatusMessage.LenderCreatingFinalClosingDocuments,
            ApplicationStatusMessage.FinalClose,
          ];
        case HighLevelStatus.Closed:
          return [];
      }
    case PortalType.LENDER:
      switch (status) {
        case HighLevelStatus.New:
          return [];
        case HighLevelStatus.InReview:
          return [
            ApplicationStatusMessage.InitialSubmission,
            ApplicationStatusMessage.IntroductoryCall,
            ApplicationStatusMessage.BorrowerReview,
            ApplicationStatusMessage.BorrowerFinalAttest,
            ApplicationStatusMessage.LenderSubmission,
          ];
        case HighLevelStatus.NonBinding:
          return [
            ApplicationStatusMessage.LenderCreatingNonBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingNonBindingOffers,
            ApplicationStatusMessage.BorrowerReviewingNonBindingOffers,
            ApplicationStatusMessage.LenderIntroCall,
          ];
        case HighLevelStatus.Binding:
          return [
            ApplicationStatusMessage.LenderCreateBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingBindingOffer,
            ApplicationStatusMessage.BorrowerReviewingBindingOffer,
            ApplicationStatusMessage.SpecialistReviewingSignedBTS,
          ];
        case HighLevelStatus.Closing:
          return [
            ApplicationStatusMessage.LenderCreatingClosingDocs,
            ApplicationStatusMessage.SpecialistReviewingClosingDocs,
            ApplicationStatusMessage.BorrowerReviewingClosingDocs,
            ApplicationStatusMessage.SpecialistReviewingSignedClosingDocs,
            ApplicationStatusMessage.LenderCreatingFinalClosingDocuments,
            ApplicationStatusMessage.FinalClose,
          ];
        case HighLevelStatus.Closed:
          return [];
      }
  }
};

export const getStatusMessageActingPortal = (
  statusMessage: ApplicationStatusMessage,
) => {
  switch (statusMessage) {
    case ApplicationStatusMessage.Missed:
      return undefined;
    case ApplicationStatusMessage.Declined:
      return undefined;
    case ApplicationStatusMessage.NotPursued:
      return undefined;
    case ApplicationStatusMessage.InitialSubmission:
      return PortalType.BORROWER;
    case ApplicationStatusMessage.IntroductoryCall:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.BorrowerReview:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.BorrowerFinalAttest:
      return PortalType.BORROWER;
    case ApplicationStatusMessage.LenderSubmission:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.LenderIntroCall:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.LenderCreatingNonBindingOffer:
      return PortalType.LENDER;
    case ApplicationStatusMessage.SpecialistReviewingNonBindingOffers:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.BorrowerReviewingNonBindingOffers:
      return PortalType.BORROWER;
    case ApplicationStatusMessage.BindingOfferNeedsRevision:
      return PortalType.LENDER;
    case ApplicationStatusMessage.LenderCreateBindingOffer:
      return PortalType.LENDER;
    case ApplicationStatusMessage.SpecialistReviewingBindingOffer:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.BorrowerReviewingBindingOffer:
      return PortalType.BORROWER;
    case ApplicationStatusMessage.SpecialistReviewingSignedBTS:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.LenderCreatingClosingDocs:
      return PortalType.LENDER;
    case ApplicationStatusMessage.SpecialistReviewingClosingDocs:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.BorrowerReviewingClosingDocs:
      return PortalType.BORROWER;
    case ApplicationStatusMessage.SpecialistReviewingSignedClosingDocs:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.LenderCreatingFinalClosingDocuments:
      return PortalType.LENDER;
    case ApplicationStatusMessage.FinalClose:
      return PortalType.MANAGEMENT;
    case ApplicationStatusMessage.NotFunded:
      return undefined;
    case ApplicationStatusMessage.Cancelled:
      return undefined;
    case ApplicationStatusMessage.Closed:
      return undefined;
    case ApplicationStatusMessage.InUnderwriting:
      return PortalType.LENDER;
    case ApplicationStatusMessage.OnHold:
      return undefined;
  }
};

export const getStatusMessageString = (
  statusMessage: ApplicationStatusMessage,
) => {
  switch (statusMessage) {
    case ApplicationStatusMessage.Missed:
      return "Missed";
    case ApplicationStatusMessage.Declined:
      return "Declined";
    case ApplicationStatusMessage.NotPursued:
      return "Missed";
    case ApplicationStatusMessage.InitialSubmission:
      return "Initial Submission";
    case ApplicationStatusMessage.IntroductoryCall:
      return "Introductory Call";
    case ApplicationStatusMessage.BorrowerReview:
      return "Borrower Background Review";
    case ApplicationStatusMessage.SupervisorApproval:
      return "Supervisor Approval";
    case ApplicationStatusMessage.BorrowerFinalAttest:
      return "Attestation";
    case ApplicationStatusMessage.LenderSubmission:
      return "Submission to Lenders";
    case ApplicationStatusMessage.LenderIntroCall:
      return "Lender Introductory Call";
    case ApplicationStatusMessage.LenderCreatingNonBindingOffer:
      return "Waiting for Offers";
    case ApplicationStatusMessage.NonBindingOfferNeedsRevision:
      return "Non-Binding Offer Revision";
    case ApplicationStatusMessage.SpecialistReviewingNonBindingOffers:
      return "Non-Binding Offer";
    case ApplicationStatusMessage.BorrowerReviewingNonBindingOffers:
      return "Non-Binding Offer Review";
    case ApplicationStatusMessage.BindingOfferNeedsRevision:
      return "Binding Offer Revision";
    case ApplicationStatusMessage.LenderCreateBindingOffer:
      return "Binding Offer";
    case ApplicationStatusMessage.SpecialistReviewingBindingOffer:
      return "Binding Offer Approval";
    case ApplicationStatusMessage.BorrowerReviewingBindingOffer:
      return "Binding Offer Review";
    case ApplicationStatusMessage.SpecialistReviewingSignedBTS:
      return "Final Terms Approval";
    case ApplicationStatusMessage.LenderCreatingClosingDocs:
      return "Closing Docs";
    case ApplicationStatusMessage.SpecialistReviewingClosingDocs:
      return "Closing Docs Approval";
    case ApplicationStatusMessage.BorrowerReviewingClosingDocs:
      return "Review Closing Docs";
    case ApplicationStatusMessage.SpecialistReviewingSignedClosingDocs:
      return "Closing Approval";
    case ApplicationStatusMessage.LenderCreatingFinalClosingDocuments:
      return "Final Closing Documents";
    case ApplicationStatusMessage.SpecialistReviewingFinalClosingDocuments:
      return "Review Final Closing Documents";
    case ApplicationStatusMessage.ConfirmingFunding:
      return "Confirm Funding";
    case ApplicationStatusMessage.FinalClose:
      return "Final Close";
    case ApplicationStatusMessage.Cancelled:
      return "Cancelled";
    case ApplicationStatusMessage.NotFunded:
      return "Not Funded";
    case ApplicationStatusMessage.Closed:
      return "Closed";
    case ApplicationStatusMessage.OnHold:
      return "On Hold";
  }
};

export const canSubmitToLenders = (application: LoanApplicationViewModel) => {
  return application.status != LoanApplicationStatus.STARTED &&
    application.status != LoanApplicationStatus.SUBMITTED &&
    application.status != LoanApplicationStatus.SPECIALIST_REVIEW &&
    application.status != LoanApplicationStatus.SPECIALIST_REVIEW_COMPLETE;
};

export const isLoanActive = (app: LoanApplicationViewModel) => {
  return app.status != LoanApplicationStatus.CLOSED &&
    app.status != LoanApplicationStatus.CANCELLED;
};

export const getLoanStatusMessage = (
  application: LoanApplicationViewModel,
  portalType: PortalType,
) => {
  const status = application.status;
  let message = ApplicationStatusMessage.Missed;
  let category = HighLevelStatus.New;
  let requiresAction = false;

  // special status
  if (portalType == PortalType.MANAGEMENT) {
    if (application.status == LoanApplicationStatus.CANCELLED) {
      return {
        message: ApplicationStatusMessage.Cancelled,
        category: HighLevelStatus.Cancelled,
        requiresAction: false,
      };
    }
    if (application.onHold) {
      category = HighLevelStatus.OnHold;
      message = ApplicationStatusMessage.OnHold;
      return {
        message,
        category,
        requiresAction: false,
      };
    }
    if (application.isInUnderwriting) {
      category = HighLevelStatus.InUnderwriting;
      message = ApplicationStatusMessage.InUnderwriting;
      return {
        message,
        category,
        requiresAction: false,
      };
    }
  }

  if (portalType == PortalType.LENDER) {
    switch (application.lenderApplicationStatus) {
      case LenderApplicationStatus.MISSED:
        return {
          message: ApplicationStatusMessage.Missed,
          category: HighLevelStatus.Missed,
          requiresAction: false,
        };
      case LenderApplicationStatus.DECLINED:
        return {
          message: ApplicationStatusMessage.Declined,
          category: HighLevelStatus.Declined,
          requiresAction: false,
        };
      case LenderApplicationStatus.NOT_PURSUED:
        return {
          message: ApplicationStatusMessage.NotPursued,
          category: HighLevelStatus.NotPursued,
          requiresAction: false,
        };
      case LenderApplicationStatus.SUBMITTED:
      case LenderApplicationStatus.PURSUED:
        break;
    }
  }

  switch (status) {
    case LoanApplicationStatus.SCREEN1:
    case LoanApplicationStatus.SCREEN3:
    case LoanApplicationStatus.STARTED:
      message = ApplicationStatusMessage.InitialSubmission;
      category = HighLevelStatus.New;
      break;
    case LoanApplicationStatus.SUBMITTED:
      category = HighLevelStatus.InReview;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.IntroductoryCall;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.IntroductoryCall;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.IntroductoryCall;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.DILIGENCE_IN_PROGRESS:
    case LoanApplicationStatus.SPECIALIST_REVIEW:
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.BorrowerReview;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.BorrowerReview;
          break;
        case PortalType.MANAGEMENT:
          if (
            (application.diligenceStatus == DiligenceStatus.COMPLETE ||
              application.diligenceStatus == DiligenceStatus.RECEIVED)
          ) {
            message = ApplicationStatusMessage.SupervisorApproval;
          } else {
            message = ApplicationStatusMessage.BorrowerReview;
          }
          requiresAction = true;
          break;
      }
      category = HighLevelStatus.InReview;
      break;
    case LoanApplicationStatus.SPECIALIST_REVIEW_COMPLETE:
      category = HighLevelStatus.InReview;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.BorrowerFinalAttest;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.BorrowerFinalAttest;
          requiresAction = true;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.BorrowerFinalAttest;
          break;
      }
      break;

    case LoanApplicationStatus.COMPLETED_SECOND_ATTESTATION:
      category = HighLevelStatus.InReview;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.LenderSubmission;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.LenderSubmission;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.LenderSubmission;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.LENDERS_REVIEW:
    case LoanApplicationStatus.LENDERS_QNA:
    case LoanApplicationStatus.SPECIALIST_OFFERS_REVIEW:
      category = HighLevelStatus.NonBinding;
      if (
        portalType == PortalType.LENDER &&
        application.currentLender?.nonBindingOfferStatus ==
        LenderOfferStatus.NEEDS_REVISION ||
        application.currentLender?.nonBindingOfferStatus ==
        LenderOfferStatus.DECLINED
      ) {
        message = ApplicationStatusMessage.NonBindingOfferNeedsRevision;
        requiresAction = true;
      } else if (
        portalType == PortalType.LENDER &&
        application.currentLender?.nonBindingOfferStatus ==
        LenderOfferStatus.NONE_SUBMITTED
      ) {
        message = ApplicationStatusMessage.LenderCreatingNonBindingOffer;
        requiresAction = true;
        break;
      } else if (
        portalType == PortalType.LENDER &&
        application.currentLender?.nonBindingOfferStatus ==
        LenderOfferStatus.BORROWER_REVIEW
      ) {
        message = ApplicationStatusMessage.BorrowerReviewingNonBindingOffers;
        break;
      } else if (
        portalType == PortalType.BORROWER &&
        application.nonBindingOfferStatus ==
        LoanApplicationOfferStatus.BORROWER_REVIEW
      ) {
        message = ApplicationStatusMessage.BorrowerReviewingNonBindingOffers;
        requiresAction = true;
        break;
      } else {
        switch (portalType) {
          case PortalType.BORROWER:
            message = ApplicationStatusMessage.LenderCreatingNonBindingOffer;
            break;
          case PortalType.LENDER:
          case PortalType.MANAGEMENT:
            if (
              application.nonBindingOfferStatus ==
              LoanApplicationOfferStatus.SPECIALIST_REVIEW
            ) {
              message =
                ApplicationStatusMessage.SpecialistReviewingNonBindingOffers;
              requiresAction = portalType == PortalType.MANAGEMENT;
            } else if (
              application.nonBindingOfferStatus ==
              LoanApplicationOfferStatus.BORROWER_REVIEW
            ) {
              message =
                ApplicationStatusMessage.BorrowerReviewingNonBindingOffers;
            } else {
              message = ApplicationStatusMessage.LenderCreatingNonBindingOffer;
            }
            break;
        }
      }
      break;
    case LoanApplicationStatus.OFFER_ACCEPTED:
      category = HighLevelStatus.NonBinding;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.LenderIntroCall;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.LenderIntroCall;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.LenderIntroCall;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.LENDER_BORROWER_CALL_COMPLETE:
    case LoanApplicationStatus.READY_FOR_BTS:
      category = HighLevelStatus.Binding;
      switch (portalType) {
        case PortalType.LENDER:
          if (
            application.currentLender?.bindingOfferStatus ==
            LenderOfferStatus.NEEDS_REVISION
          ) {
            message = ApplicationStatusMessage.BindingOfferNeedsRevision;
            requiresAction = true;
          } else {
            message = ApplicationStatusMessage.LenderCreateBindingOffer;
            requiresAction = true;
          }
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.LenderCreateBindingOffer;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.LenderCreateBindingOffer;
          break;
      }
      break;
    case LoanApplicationStatus.SPECIALIST_BTS_REVIEW:
      category = HighLevelStatus.Binding;
      if (
        portalType == PortalType.LENDER &&
        application.currentLender?.bindingOfferStatus ==
        LenderOfferStatus.NEEDS_REVISION
      ) {
        message = ApplicationStatusMessage.BindingOfferNeedsRevision;
        requiresAction = true;
      } else {
        switch (portalType) {
          case PortalType.LENDER:
            message = ApplicationStatusMessage.SpecialistReviewingBindingOffer;
            break;
          case PortalType.BORROWER:
            message = ApplicationStatusMessage.SpecialistReviewingBindingOffer;
            break;
          case PortalType.MANAGEMENT:
            message = ApplicationStatusMessage.SpecialistReviewingBindingOffer;
            requiresAction = true;
            break;
        }
      }
      break;
    case LoanApplicationStatus.BTS_SUBMITTED:
      category = HighLevelStatus.Binding;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.BorrowerReviewingBindingOffer;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.BorrowerReviewingBindingOffer;
          requiresAction = true;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.BorrowerReviewingBindingOffer;
          break;
      }
      break;
    case LoanApplicationStatus.BTS_SIGNED_BY_BORROWER:
      category = HighLevelStatus.Binding;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.SpecialistReviewingSignedBTS;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.SpecialistReviewingSignedBTS;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.SpecialistReviewingSignedBTS;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.SIGNED_BTS_APPROVED:
      category = HighLevelStatus.Closing;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.LenderCreatingClosingDocs;
          requiresAction = true;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.LenderCreatingClosingDocs;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.LenderCreatingClosingDocs;
          break;
      }
      break;
    case LoanApplicationStatus.SPECIALIST_CLOSING_DOCUMENTS_REVIEW:
      category = HighLevelStatus.Closing;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.SpecialistReviewingClosingDocs;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.SpecialistReviewingClosingDocs;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.SpecialistReviewingClosingDocs;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.CLOSING_DOCUMENTS_SUBMITTED:
      category = HighLevelStatus.Closing;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.BorrowerReviewingClosingDocs;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.BorrowerReviewingClosingDocs;
          requiresAction = true;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.BorrowerReviewingClosingDocs;
          break;
      }
      break;
    case LoanApplicationStatus.CLOSING_DOCUMENTS_SIGNED:
      category = HighLevelStatus.Closing;
      switch (portalType) {
        case PortalType.LENDER:
          message =
            ApplicationStatusMessage.SpecialistReviewingSignedClosingDocs;
          break;
        case PortalType.BORROWER:
          message =
            ApplicationStatusMessage.SpecialistReviewingSignedClosingDocs;
          break;
        case PortalType.MANAGEMENT:
          message =
            ApplicationStatusMessage.SpecialistReviewingSignedClosingDocs;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.SIGNED_CLOSING_DOCUMENTS_APPROVED:
      category = HighLevelStatus.Closing;
      switch (portalType) {
        case PortalType.LENDER:
          message =
            ApplicationStatusMessage.LenderCreatingFinalClosingDocuments;
          requiresAction = true;
          break;
        case PortalType.BORROWER:
          message =
            ApplicationStatusMessage.LenderCreatingFinalClosingDocuments;
          break;
        case PortalType.MANAGEMENT:
          message =
            ApplicationStatusMessage.LenderCreatingFinalClosingDocuments;
          break;
      }
      break;
    case LoanApplicationStatus.SPECIALIST_FINAL_CLOSING_DOCUMENTS_REVIEW:
      category = HighLevelStatus.Closing;
      switch (portalType) {
        case PortalType.LENDER:
          message =
            ApplicationStatusMessage.SpecialistReviewingFinalClosingDocuments;
          break;
        case PortalType.BORROWER:
          message =
            ApplicationStatusMessage.SpecialistReviewingFinalClosingDocuments;
          break;
        case PortalType.MANAGEMENT:
          message =
            ApplicationStatusMessage.SpecialistReviewingFinalClosingDocuments;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.SPECIALIST_FINAL_CLOSING_DOCUMENTS_APPROVED:
      category = HighLevelStatus.Closing;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.ConfirmingFunding;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.ConfirmingFunding;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.ConfirmingFunding;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.LOAN_FUNDED:
      category = HighLevelStatus.Closing;
      switch (portalType) {
        case PortalType.LENDER:
          message = ApplicationStatusMessage.FinalClose;
          break;
        case PortalType.BORROWER:
          message = ApplicationStatusMessage.FinalClose;
          break;
        case PortalType.MANAGEMENT:
          message = ApplicationStatusMessage.FinalClose;
          requiresAction = true;
          break;
      }
      break;
    case LoanApplicationStatus.REJECTED:
      category = HighLevelStatus.Declined;
      message = ApplicationStatusMessage.Declined;
      break;
    case LoanApplicationStatus.NOT_FUNDED:
      message = ApplicationStatusMessage.NotFunded;
      category = HighLevelStatus.NotFunded;
      break;
    case LoanApplicationStatus.CANCELLED:
      message = ApplicationStatusMessage.Cancelled;
      category = HighLevelStatus.Cancelled;
      break;
    case LoanApplicationStatus.CLOSED:
      message = ApplicationStatusMessage.Closed;
      category = HighLevelStatus.Closed;
      break;
  }
  return {
    category: category,
    message: message,
    requiresAction: requiresAction,
  };
};
